import { createContext, useState, useContext, useCallback, useMemo, FC } from 'react';
import { noop } from 'lodash';
// Import Types
import type { StepValues, StepId, StepperContextValues } from './types';

const StepperContext = createContext<StepperContextValues>({ allStepsValues: {}, setAllStepsValues: noop });

interface StepperProviderProps {
    stepsInitialValues: Record<StepId, StepValues>;
}

export const StepperProvider: FC<StepperProviderProps> = ({ children, stepsInitialValues }) => {
    const [allStepsValues, setAllStepsValues] = useState(stepsInitialValues);

    return <StepperContext.Provider value={{ allStepsValues, setAllStepsValues }}>{children}</StepperContext.Provider>;
};

export const useStepperContext = () => {
    const context = useContext(StepperContext);
    if (!context) throw new Error('useStepperContext must be used within a StepperProvider');
    return context;
};

export const useStepValues = (stepId: StepId) => {
    const context = useContext(StepperContext);
    if (!context) throw new Error('useStepperContext must be used within a StepperProvider');

    const { allStepsValues, setAllStepsValues } = context;
    const stepValues = allStepsValues[stepId];

    const setStepValues = useCallback(
        (newValues: StepValues) => {
            setAllStepsValues((prev) => ({ ...prev, [stepId]: newValues }));
        },
        [setAllStepsValues, stepId],
    );

    return useMemo(
        () => ({
            stepValues,
            setStepValues,
        }),
        [setStepValues, stepValues],
    );
};

const ActiveStepActionsContext = createContext<{ handleClear?: () => void; handleReset?: () => void }>({});

export const ActiveStepActionsProvider = ActiveStepActionsContext.Provider;

export const useActiveStepActions = () => {
    const context = useContext(ActiveStepActionsContext);
    if (!context) throw new Error('useActiveStepActions must be used within a ActiveStepActionsProvider');
    return context;
};
